import type { PixelIconName } from '@/components/ui/PixelIcon';
import type { ShopManager } from '@/game/shopManager';
import type { PropEffect } from '@/types/props';
import { getLifeManager } from '@/services/lifeManager';

export type UpgradeId = 'coinFinder' | 'magnetCore' | 'lifeVault';

export type UpgradeCurrency = 'coins' | 'gems';

export interface UpgradeDefinition {
  id: UpgradeId;
  name: string;
  description: string;
  icon: PixelIconName;
  currency: UpgradeCurrency;
  maxLevel: number;
  baseCost: number;
  costGrowth: number; // multiplicative per level purchase (>= 1.0)
  // Effect is either represented as a PropEffect (re-using the existing system)
  // or as a side-effect (e.g. increasing max lives).
  effect:
    | { kind: 'propEffect'; type: PropEffect['type']; valueAtLevel: (level: number) => number; descriptionAtLevel: (level: number) => string }
    | { kind: 'maxLives'; bonusPerLevel: number };
}

export interface UpgradeState {
  version: 1;
  levels: Record<UpgradeId, number>;
}

const STORAGE_KEY = 'tmh_upgrades_v1';

export const UPGRADE_DEFS: UpgradeDefinition[] = [
  {
    id: 'coinFinder',
    name: 'Coin Finder',
    description: 'Permanent coin and gem value multiplier.',
    icon: 'coin',
    currency: 'coins',
    maxLevel: 5,
    baseCost: 120,
    costGrowth: 1.65,
    effect: {
      kind: 'propEffect',
      type: 'luck',
      valueAtLevel: (level) => 1 + level * 0.15,
      descriptionAtLevel: (level) => `+${Math.round(level * 15)}% coin value`,
    },
  },
  {
    id: 'magnetCore',
    name: 'Magnet Core',
    description: 'Auto-collect nearby coins every move.',
    icon: 'magnet',
    currency: 'gems',
    maxLevel: 3,
    baseCost: 4,
    costGrowth: 1.75,
    effect: {
      kind: 'propEffect',
      type: 'magnet',
      valueAtLevel: (level) => level, // Manhattan range
      descriptionAtLevel: (level) => `Range ${level}`,
    },
  },
  {
    id: 'lifeVault',
    name: 'Life Vault',
    description: 'Increase your maximum lives cap.',
    icon: 'life',
    currency: 'gems',
    maxLevel: 3,
    baseCost: 6,
    costGrowth: 1.65,
    effect: { kind: 'maxLives', bonusPerLevel: 1 },
  },
];

function clampLevel(def: UpgradeDefinition, level: number) {
  return Math.max(0, Math.min(def.maxLevel, Math.floor(level)));
}

function getDefaultState(): UpgradeState {
  return {
    version: 1,
    levels: {
      coinFinder: 0,
      magnetCore: 0,
      lifeVault: 0,
    },
  };
}

export class UpgradeManager {
  private state: UpgradeState;
  private updateCallbacks: Set<() => void> = new Set();

  constructor() {
    this.state = this.loadState();
    this.applySideEffects();
  }

  onUpdate(cb: () => void): () => void {
    this.updateCallbacks.add(cb);
    return () => this.updateCallbacks.delete(cb);
  }

  private notify() {
    for (const cb of this.updateCallbacks) cb();
  }

  getState(): UpgradeState {
    return JSON.parse(JSON.stringify(this.state)) as UpgradeState;
  }

  getLevel(id: UpgradeId): number {
    const def = UPGRADE_DEFS.find((d) => d.id === id);
    const raw = this.state.levels[id] ?? 0;
    return def ? clampLevel(def, raw) : raw;
  }

  getDefinition(id: UpgradeId): UpgradeDefinition | undefined {
    return UPGRADE_DEFS.find((d) => d.id === id);
  }

  getCostForNextLevel(id: UpgradeId): number | null {
    const def = this.getDefinition(id);
    if (!def) return null;
    const current = this.getLevel(id);
    if (current >= def.maxLevel) return null;

    const cost = Math.round(def.baseCost * Math.pow(def.costGrowth, current));
    return Math.max(1, cost);
  }

  purchase(id: UpgradeId, shopManager: ShopManager): { ok: boolean; reason?: string } {
    const def = this.getDefinition(id);
    if (!def) return { ok: false, reason: 'Unknown upgrade' };

    const current = this.getLevel(id);
    if (current >= def.maxLevel) return { ok: false, reason: 'Max level' };

    const cost = this.getCostForNextLevel(id);
    if (!cost) return { ok: false, reason: 'No cost' };

    const canAfford =
      def.currency === 'coins' ? shopManager.coins >= cost : shopManager.gems >= cost;
    if (!canAfford) return { ok: false, reason: 'Insufficient funds' };

    const spent = def.currency === 'coins' ? shopManager.spendCoins(cost) : shopManager.spendGems(cost);
    if (!spent) return { ok: false, reason: 'Spend failed' };

    this.state.levels[id] = current + 1;
    this.saveState();
    this.applySideEffects();
    this.notify();
    return { ok: true };
  }

  getPropEffects(): PropEffect[] {
    const effects: PropEffect[] = [];
    for (const def of UPGRADE_DEFS) {
      const level = this.getLevel(def.id);
      if (level <= 0) continue;
      if (def.effect.kind !== 'propEffect') continue;

      effects.push({
        type: def.effect.type,
        value: def.effect.valueAtLevel(level),
        description: def.effect.descriptionAtLevel(level),
      });
    }
    return effects;
  }

  getMaxLivesBonus(): number {
    const def = this.getDefinition('lifeVault');
    if (!def || def.effect.kind !== 'maxLives') return 0;
    const level = this.getLevel('lifeVault');
    return def.effect.bonusPerLevel * level;
  }

  private applySideEffects() {
    const lifeManager = getLifeManager();
    const baseMax = 5;
    const bonus = this.getMaxLivesBonus();
    lifeManager.setMaxLives(baseMax + bonus);
  }

  private loadState(): UpgradeState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return getDefaultState();
      const parsed = JSON.parse(raw) as Partial<UpgradeState>;
      if (!parsed || parsed.version !== 1 || typeof parsed.levels !== 'object' || !parsed.levels) {
        return getDefaultState();
      }
      const next: UpgradeState = getDefaultState();
      for (const def of UPGRADE_DEFS) {
        const v = (parsed.levels as any)[def.id];
        next.levels[def.id] = clampLevel(def, typeof v === 'number' ? v : 0);
      }
      return next;
    } catch {
      return getDefaultState();
    }
  }

  private saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // ignore
    }
  }
}

let upgradeManagerSingleton: UpgradeManager | null = null;

export function getUpgradeManager(): UpgradeManager {
  if (!upgradeManagerSingleton) upgradeManagerSingleton = new UpgradeManager();
  return upgradeManagerSingleton;
}

